import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { submitAssessment } from "@/lib/api";

const INDUSTRIES = [
    "SaaS / Software",
    "Servicios Financieros",
    "Salud / Life Sciences",
    "Manufactura / Industrial",
    "Retail / E-commerce",
    "Servicios Profesionales",
    "Otro",
];
const SIZES = ["1–50", "51–200", "201–1,000", "1,001–5,000", "5,000+"];
const USAGE = [
    "Ninguno, explorando",
    "Solo pilotos",
    "Algunos casos en producción",
    "IA integrada en varias funciones",
];
const PAINS = [
    "Velocidad del pipeline",
    "Presión sobre coste y margen",
    "Carga manual en operaciones",
    "Calidad o fragmentación de los datos",
    "Volumen de atención al cliente",
    "Precisión de la previsión de ventas",
    "Brecha de talento o contratación",
    "Gobierno y riesgo",
];
const GOALS = [
    "Acortar el ciclo de venta",
    "Subir la retención neta de ingresos",
    "Automatizar flujos internos",
    "Lanzar un producto nativo de IA",
    "Reducir el coste de servir",
    "Modernizar la base de datos",
];
const MATURITY = [
    "Hojas de cálculo y conocimiento informal",
    "Sistemas mixtos, visibilidad parcial",
    "Almacén centralizado, gobierno básico",
    "Plataforma de datos madura con gobierno",
];
const BUDGETS = ["< $50k", "$50k – $250k", "$250k – $1M", "$1M+"];
const TIMELINES = ["Este trimestre", "En 6 meses", "En 12 meses", "Explorando"];

const STEPS = [
    { id: "industria", label: "Tu industria", type: "single", key: "industry", options: INDUSTRIES },
    { id: "tamaño", label: "Tamaño de la empresa", type: "single", key: "company_size", options: SIZES },
    { id: "uso", label: "Uso actual de la IA", type: "single", key: "current_ai_usage", options: USAGE },
    { id: "dolor", label: "Principales puntos de dolor", type: "multi", key: "pain_points", options: PAINS, max: 3 },
    { id: "objetivos", label: "Objetivos a 12 meses", type: "multi", key: "goals", options: GOALS, max: 3 },
    { id: "datos", label: "Madurez de tus datos", type: "single", key: "data_maturity", options: MATURITY },
    { id: "presupuesto", label: "Rango de presupuesto (opcional)", type: "single", key: "budget_band", options: BUDGETS, optional: true },
    { id: "plazo", label: "Plazo (opcional)", type: "single", key: "timeline", options: TIMELINES, optional: true },
];

export default function Assessment() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({
        industry: "",
        company_size: "",
        current_ai_usage: "",
        pain_points: [],
        goals: [],
        data_maturity: "",
        budget_band: "",
        timeline: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const current = STEPS[step];
    const total = STEPS.length;
    const progress = ((step + 1) / total) * 100;

    const pick = (val) => {
        if (current.type === "single") {
            setAnswers((a) => ({ ...a, [current.key]: val }));
        } else {
            setAnswers((a) => {
                const arr = a[current.key];
                const has = arr.includes(val);
                if (has) return { ...a, [current.key]: arr.filter((x) => x !== val) };
                if (arr.length >= (current.max || 99)) return a;
                return { ...a, [current.key]: [...arr, val] };
            });
        }
    };

    const canAdvance = () => {
        if (current.optional) return true;
        const v = answers[current.key];
        if (current.type === "multi") return Array.isArray(v) && v.length > 0;
        return !!v;
    };

    const next = async () => {
        if (!canAdvance()) {
            toast.error("Selecciona al menos una opción para continuar.");
            return;
        }
        if (step < total - 1) {
            setStep(step + 1);
            return;
        }
        // submit
        setSubmitting(true);
        try {
            const data = await submitAssessment(answers);
            setResult(data);
        } catch (err) {
            toast.error("No se pudo generar el reporte ahora. Inténtalo de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    const back = () => {
        if (step === 0) return;
        setStep(step - 1);
    };

    const isSelected = (val) => {
        const v = answers[current.key];
        return current.type === "multi" ? v.includes(val) : v === val;
    };

    if (result) {
        return <ResultView result={result} onRestart={() => { setResult(null); setStep(0); setAnswers({
            industry: "", company_size: "", current_ai_usage: "", pain_points: [], goals: [],
            data_maturity: "", budget_band: "", timeline: "",
        }); }} />;
    }

    return (
        <div data-testid="assessment-page" className="min-h-[80vh]">
            <section className="container-x pt-12 pb-8 lg:pt-16 border-b border-brand-line">
                <div className="flex items-center justify-between mb-6">
                    <div className="label-mono">Diagnóstico de Crecimiento con IA</div>
                    <div className="label-mono">
                        Paso {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                    </div>
                </div>
                <div className="h-px bg-brand-line relative overflow-hidden">
                    <div
                        className="absolute left-0 top-0 h-px bg-brand-electric transition-all duration-500"
                        style={{ width: `${progress}%` }}
                        data-testid="assessment-progress"
                    />
                </div>
            </section>

            <section className="container-x py-16 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="label-mono">{current.id.toUpperCase()}</div>
                        <h1 className="font-heading text-4xl lg:text-5xl mt-6 leading-tight">
                            {current.label}
                        </h1>
                        <p className="mt-6 text-brand-gray leading-relaxed max-w-md">
                            {current.type === "multi"
                                ? `Elige hasta ${current.max || "las que apliquen"}.`
                                : "Elige la opción que mejor describe tu contexto."}
                            {current.optional && " Esta pregunta es opcional."}
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {current.options.map((opt) => {
                                const sel = isSelected(opt);
                                return (
                                    <button
                                        key={opt}
                                        type="button"
                                        onClick={() => pick(opt)}
                                        data-testid={`assessment-opt-${opt
                                            .replace(/\s+/g, "-")
                                            .toLowerCase()
                                            .replace(/[^a-z0-9-]/g, "")}`}
                                        className={`text-left border px-5 py-4 transition-all flex items-center justify-between gap-3 ${
                                            sel
                                                ? "border-brand-electric bg-brand-navy text-white"
                                                : "border-brand-line bg-white text-brand-navy hover:border-brand-navy"
                                        }`}
                                    >
                                        <span className="text-sm font-medium">{opt}</span>
                                        {sel && <Check size={16} />}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-12 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={back}
                                disabled={step === 0}
                                data-testid="assessment-back"
                                className="btn-ghost disabled:opacity-30"
                            >
                                <ArrowLeft size={16} /> Atrás
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                disabled={submitting}
                                data-testid="assessment-next"
                                className="btn-primary disabled:opacity-60"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} /> Generando reporte
                                    </>
                                ) : step === total - 1 ? (
                                    <>
                                        Generar mi reporte <ArrowRight size={16} />
                                    </>
                                ) : (
                                    <>
                                        Siguiente <ArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ResultView({ result, onRestart }) {
    const score = Math.max(0, Math.min(100, result.score || 0));
    return (
        <div className="container-x pt-12 pb-24 lg:pt-16 lg:pb-32" data-testid="assessment-result">
            <div className="label-mono">Informe · Índice de Crecimiento con IA</div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-8">
                <div className="lg:col-span-5">
                    <div className="border border-brand-line p-8 lg:p-10">
                        <div className="label-mono">Score</div>
                        <div className="flex items-end gap-3 mt-4">
                            <div
                                className="font-heading text-[120px] leading-none text-brand-navy tracking-tighter"
                                data-testid="result-score"
                            >
                                {score}
                            </div>
                            <div className="text-brand-gray mb-5">/ 100</div>
                        </div>
                        <div className="mt-6 h-1 bg-brand-line">
                            <div
                                className="h-full bg-brand-electric transition-all"
                                style={{ width: `${score}%` }}
                            />
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                            <span className="label-mono">Tier</span>
                            <span className="font-heading text-xl text-brand-navy" data-testid="result-tier">
                                {result.tier}
                            </span>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/contact" data-testid="result-cta-contact" className="btn-primary">
                            Agenda una reunión <ArrowRight size={16} />
                        </Link>
                        <button onClick={onRestart} data-testid="result-restart" className="btn-secondary">
                            Reiniciar diagnóstico
                        </button>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <h2 className="font-heading text-3xl lg:text-4xl leading-tight">
                        Lectura ejecutiva
                    </h2>
                    <p className="mt-5 text-brand-navy text-lg leading-relaxed" data-testid="result-summary">
                        {result.summary}
                    </p>

                    <div className="mt-12">
                        <div className="label-mono">Recomendaciones</div>
                        <div className="mt-4 divide-y divide-brand-line border-y border-brand-line">
                            {(result.recommendations || []).map((r, i) => (
                                <div key={i} className="py-6 grid grid-cols-12 gap-6" data-testid={`result-rec-${i}`}>
                                    <div className="col-span-1 font-mono text-brand-electric text-sm">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="col-span-11">
                                        <div className="font-heading text-xl text-brand-navy">
                                            {r.title}
                                        </div>
                                        <div className="mt-2 text-brand-gray leading-relaxed">
                                            {r.detail}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-12">
                        <div className="label-mono">Próximos pasos</div>
                        <ul className="mt-4 space-y-3">
                            {(result.next_steps || []).map((s, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-4 text-brand-navy"
                                    data-testid={`result-next-${i}`}
                                >
                                    <Check className="text-brand-electric mt-1 shrink-0" size={16} />
                                    <span>{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
